import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { useState } from "react";

export type ConfirmVariant = "primary" | "danger" | "success" | "info";

type ConfirmModalProps = {
	open: boolean;
	title: string;
	message: string;
	onConfirm: () => Promise<void> | void;
	onCancel: () => void;
	confirmLabel?: string;
	cancelLabel?: string;
	variant?: ConfirmVariant;
};

const variantStyles: Record<ConfirmVariant, { badge: string; iconColor: string; confirm: string; confirmHover: string; Icon: typeof Info }> = {
	primary: {
		badge: "bg-primary/10 text-primary",
		iconColor: "text-primary",
		confirm: "bg-primary text-white",
		confirmHover: "hover:bg-primary-hover",
		Icon: Info,
	},
	danger: {
		badge: "bg-rose-100 text-rose-500",
		iconColor: "text-rose-500",
		confirm: "bg-rose-500 text-white",
		confirmHover: "hover:bg-rose-600",
		Icon: AlertTriangle,
	},
	success: {
		badge: "bg-emerald-100 text-emerald-600",
		iconColor: "text-emerald-600",
		confirm: "bg-emerald-500 text-white",
		confirmHover: "hover:bg-emerald-600",
		Icon: CheckCircle2,
	},
	info: {
		badge: "bg-slate-100 text-slate-500",
		iconColor: "text-slate-500",
		confirm: "bg-slate-700 text-white",
		confirmHover: "hover:bg-slate-900",
		Icon: Info,
	},
};

export function ConfirmModal({
	open,
	title,
	message,
	onConfirm,
	onCancel,
	confirmLabel = "Confirmar",
	cancelLabel = "Cancelar",
	variant = "primary",
}: ConfirmModalProps) {
	const [isProcessing, setIsProcessing] = useState(false);

	if (!open) return null;

	const { badge, iconColor, confirm, confirmHover, Icon } = variantStyles[variant];

	const handleConfirm = async () => {
		try {
			setIsProcessing(true);
			await onConfirm();
		} finally {
			setIsProcessing(false);
		}
	};

	return (
		<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6' onClick={onCancel}>
			<div
				className='relative w-full max-w-sm max-h-[90vh] overflow-y-auto rounded-2xl bg-white px-5 sm:px-6 py-5 soft-border card-shadow'
				onClick={(event) => event.stopPropagation()}
			>
				<button
					type='button'
					onClick={onCancel}
					aria-label='Cerrar'
					className='absolute right-4 top-4 rounded-full p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 cursor-pointer'
				>
					<X className='h-4 w-4' />
				</button>

				<div className='flex items-start gap-3'>
					<span className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${badge}`}>
						<Icon className={`h-5 w-5 ${iconColor}`} />
					</span>
					<div>
						<h2 className='text-base font-semibold text-slate-900'>{title}</h2>
						<p className='mt-1 text-sm text-slate-500'>{message}</p>
					</div>
				</div>

				<div className='mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3'>
					<button
						type='button'
						onClick={onCancel}
						className='inline-flex cursor-pointer items-center justify-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50'
					>
						{cancelLabel}
					</button>
					<button
						type='button'
						onClick={handleConfirm}
						disabled={isProcessing}
						className={`inline-flex cursor-pointer items-center justify-center rounded-full px-4 py-2 text-sm font-semibold ${confirm} ${confirmHover} disabled:opacity-60`}
					>
						{isProcessing ? "Procesando..." : confirmLabel}
					</button>
				</div>
			</div>
		</div>
	);
}
